import { Dtos, formatAddress } from "@inrev/common";
import { FC } from "react";
import { CopyableText } from "../../patterns/copyable-text";
import { Icon } from "../../primitives/Icon";
import { TruncatedLabel } from "../../ui/TruncatedLabel";
import { DetailCard } from "./DetailCard";
import { DetailStatusCell, type DetailStatusCellProps } from "./DetailStatusCell";
import { useDeleteContactConfirmation } from "./useDeleteContactConfirmation";

const EMPTY_STATUS_CELLS: DetailStatusCellProps[] = [];

type Company = Dtos.Company.Get.Response | Dtos.Admin.SuretyAccount.Get.Response.Company;

export type CompanyContactCardProps = {
	company: Company;
	statusCells?: DetailStatusCellProps[];
	onEdit?: () => void;
	onDelete?: () => void;
	deleteSubMessage?: string;
};

export const CompanyContactCard: FC<CompanyContactCardProps> = ({
	company,
	statusCells = EMPTY_STATUS_CELLS,
	onEdit,
	onDelete,
	deleteSubMessage,
}) => {
	const { name, fein } = company;
	const address = formatAddress(company.address);
	const { requestDelete, confirmationModal } = useDeleteContactConfirmation(
		name,
		onDelete,
		deleteSubMessage,
	);

	return (
		<>
			<DetailCard
				onEdit={onEdit}
				onDelete={requestDelete}
				title={
					<TruncatedLabel
						mask
						label={name}
						className="text-[15px] font-medium text-primary min-w-0"
					/>
				}
			>
				<div className="flex flex-col mt-[6px] space-y-[4px] text-[14px] text-secondary min-w-0">
					<div className="flex items-center space-x-[8px] min-w-0">
						<Icon type="map-pin" color="tertiary" size="mini" className="stroke-[1.75] shrink-0" />
						<CopyableText mask value={address} />
					</div>
					<div className="flex items-center space-x-[8px] min-w-0">
						<span className="w-[16px] text-[9px] font-medium text-tertiary italic shrink-0">
							FEIN
						</span>
						{fein ? <CopyableText mask value={fein} /> : <span className="text-tertiary">--</span>}
					</div>
				</div>
				{statusCells.length > 0 && (
					<div className="flex items-center justify-around mt-[4px] pt-[4px]">
						{statusCells.map((cell) => (
							<DetailStatusCell key={cell.label} {...cell} />
						))}
					</div>
				)}
			</DetailCard>
			{confirmationModal}
		</>
	);
};
